import { Button, Card, CardContent, Chip, CircularProgress, Skeleton } from "@mui/material";
import { ReactNode } from "react";
import { MdInsertChartOutlined, MdRefresh } from "react-icons/md";

interface ChartCardProps {
  title: ReactNode;
  description?: ReactNode;
  /**
   * За какой период показаны данные («За год», «Последние 12 мес.», «За всё время»).
   * Помогает понять, какие графики реагируют на общий переключатель периода.
   */
  periodLabel?: string;
  /** Дополнительный элемент справа от заголовка (итог, ссылка и т.п.). */
  aside?: ReactNode;
  loading?: boolean;
  /** Фоновое обновление — оставляем старый график, показываем маленький спиннер. */
  fetching?: boolean;
  error?: unknown;
  onRetry?: () => void;
  /** Данных нет — вместо пустого графика показываем заглушку. */
  empty?: boolean;
  emptyText?: string;
  /** Высота области графика, px (чтобы карточка не «прыгала» при загрузке). */
  height?: number;
  children: ReactNode;
}

/** Единая обёртка для графиков дашборда. */
const ChartCard = ({
  title,
  description,
  periodLabel,
  aside,
  loading,
  fetching,
  error,
  onRetry,
  empty,
  emptyText = "Нет данных за выбранный период",
  height = 280,
  children,
}: ChartCardProps) => {
  const renderBody = () => {
    if (loading) {
      return <Skeleton variant="rounded" height={height} />;
    }
    if (error) {
      return (
        <div className="chart-card__state">
          <div>Не удалось загрузить данные</div>
          {onRetry && (
            <Button size="small" startIcon={<MdRefresh />} onClick={onRetry}>
              Повторить
            </Button>
          )}
        </div>
      );
    }
    if (empty) {
      return (
        <div className="chart-card__state">
          <MdInsertChartOutlined size={32} />
          <div>{emptyText}</div>
        </div>
      );
    }
    return children;
  };

  return (
    <Card className="chart-card">
      <CardContent>
        <div className="chart-card__header">
          <div className="chart-card__heading">
            <h4 className="chart-card__title">
              {title}
              {fetching && !loading && <CircularProgress size={12} className="chart-card__spinner" />}
            </h4>
            {description && <p className="chart-card__description">{description}</p>}
          </div>
          <div className="chart-card__aside">
            {aside}
            {periodLabel && <Chip size="small" variant="outlined" label={periodLabel} className="chart-card__period" />}
          </div>
        </div>
        <div className="chart-card__area" style={{ height }}>
          {renderBody()}
        </div>
      </CardContent>
    </Card>
  );
};

export default ChartCard;
